import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import { Actions as ADMIN_ACTIONS } from '../../store/actions/adminActions';
import { Actions as CONSUMPTION_ACTIONS } from '../../store/actions/consumptionActions';
import { Actions as FACILITY_ACTIONS } from '../../store/actions/facilityActions';
import { Actions as REPORT_ACTIONS } from '../../store/actions/reportActions';
import { Actions as DASHBOARD_ACTIONS } from '../../store/actions/dashboardActions';
import BenchmarkReport from './BenchmarkReport';
import EmissionsByMainGreenhouseGases from './EmissionsByMainGreenhouseGases';
import EmissionsDisaggregatedByCustomCategory from './EmissionsDisaggregatedByCustomCategory';
import EmissionsDisaggregatedByFacility from './EmissionsDisaggregatedByFacility';
import EmissionsDisaggregatedByOrganisation from './EmissionsDisaggregatedByOrganisation';
import GreenhouseEmissionsBySource from './GreenhouseEmissionsBySource';
import ReportOverview from './ReportOverview';
import TotalEmissionsRatios from './TotalEmissionsRatios';
import ElectricityRenewable from '../ConsumptionReport/ElectricityRenewable';
import WasteRecyclable from '../ConsumptionReport/WasteRecyclable';
import { NetZeroChart } from '../NetZero/NetZeroTarget';
import { getReportingYearDates, getMultiYearChartDates } from '../Common/ReusableComponents/Reusable';
import { Button } from '../ui/button';
import _ from 'lodash';

const getBaseYearEmissions = (target) => {
  if (target?.settingMethod === 'Physical intensity convergence' && target?.baseYearOutput) {
    return Number(target.baseYearEmissions) / Number(target.baseYearOutput);
  }
  return Number(target?.baseYearEmissions);
};

const HTMLReport = ({
  report,
  consumptionFilter,
  getFacilityMetrics,
  getReport,
  getBenchmarkData,
  fetchingConsumpData,
  branding,
  facilitiesExcluded,
  usageTypeDetails,
  getAllItemsFromDB,
  totals,
  orgTargets,
  sumValues,
  currentOrganisation,
  orgDetails,
  multiYearData,
}) => {
  const { headers } = _.get(branding, 'fonts[0].font', {});
  const filterClasses = consumptionFilter.map((item) => `hide-${item}`);
  const [showFab, setShowFab] = useState(false);
  const multiYearFetchKeyRef = useRef(null);

  const reportYearTargets = useMemo(
    () => (orgTargets || []).filter((target) => Number(target?.baseYear) === Number(report?.year)),
    [orgTargets, report?.year],
  );

  useEffect(() => {
    const reportId = window.location.pathname.split('/')[3];
    getReport(reportId);
  }, [getReport]);

  useEffect(() => {
    (async () => {
      if (report && report.id) {
        await getBenchmarkData();
        await getFacilityMetrics();
      }
      if (!usageTypeDetails) {
        await getAllItemsFromDB('usageTypeDetails');
      }
    })();
    //eslint-disable-next-line
  }, [report && report.id]);

  useEffect(() => {
    if (!report?.year || !currentOrganisation || !orgDetails || reportYearTargets.length === 0) return;

    // SAVE_SUM cloneDeeps the whole store, so orgDetails gets a new reference every fetch.
    // Gate on a stable key so we only request multi-year data once per report/org.
    const fetchKey = `${currentOrganisation}_${report.year}_location`;
    if (multiYearFetchKeyRef.current === fetchKey) return;
    multiYearFetchKeyRef.current = fetchKey;

    const year = report.year;
    const currentYear = new Date().getFullYear();
    const calculatedDates = getReportingYearDates(year, orgDetails, false);
    const multiYearDates = getMultiYearChartDates(currentYear, orgDetails);
    const isCalendarYearOrg = orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;
    const dataType = 'location';

    const data = [
      {
        group_by: 'entity',
        field: isCalendarYearOrg
          ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
          : [
              `${dataType}.reportingYear.scope.3`,
              `${dataType}.reportingYear.scope.2`,
              `${dataType}.reportingYear.scope.1`,
            ],
        beginningStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
        endStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
        beginningEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
        endEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
        chart: 'yearly',
      },
      {
        group_by: isCalendarYearOrg ? `${dataType}.calendarYear.year` : `${dataType}.reportingYear.year`,
        field: isCalendarYearOrg
          ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
          : [
              `${dataType}.reportingYear.scope.3`,
              `${dataType}.reportingYear.scope.2`,
              `${dataType}.reportingYear.scope.1`,
            ],
        beginningStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
        endStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
        beginningEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
        endEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
        chart: 'multiYear',
      },
    ];

    sumValues(data, year, currentOrganisation, dataType);
    //eslint-disable-next-line
  }, [report?.year, currentOrganisation, reportYearTargets.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight) {
        setShowFab(true);
      } else {
        setShowFab(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!report || !report.id) {
    return (
      <div className='flex items-center justify-center w-full h-40 bg-background text-foreground'>
        <div className='w-full max-w-md'>
          <div className='h-2 bg-muted animate-pulse rounded mb-2'></div>
          <div className='h-2 bg-muted animate-pulse rounded mb-2'></div>
          <div className='h-2 bg-muted animate-pulse rounded'></div>
        </div>
      </div>
    );
  }

  return (
    <div className={filterClasses.join(' ')} style={{ fontFamily: headers }}>
      <ReportOverview />
      {fetchingConsumpData || !totals ? (
        <div className='flex flex-col items-center justify-center py-8 bg-muted text-muted-foreground rounded'>
          <span className='mb-2'>Loading Report...</span>
          <div className='w-48 h-2 bg-muted animate-pulse rounded'></div>
        </div>
      ) : (
        <section className='bg-card text-card-foreground rounded shadow-md relative my-4'>
          <div className='p-4 overflow-x-auto'>
            <TotalEmissionsRatios />
            <GreenhouseEmissionsBySource />
            <EmissionsByMainGreenhouseGases />
            <EmissionsDisaggregatedByOrganisation />
            <EmissionsDisaggregatedByFacility />
            <EmissionsDisaggregatedByCustomCategory />
            <WasteRecyclable />
            <ElectricityRenewable />
            <BenchmarkReport />
            {reportYearTargets.length > 0 &&
              reportYearTargets.map((target) => (
                <div key={target.id} className='my-8'>
                  <NetZeroChart
                    baseYear={target.baseYear}
                    targetYear={target.targetYear}
                    reduction={target.absoluteReduction}
                    targetCoverage={target.targetCoverage}
                    data={getBaseYearEmissions(target)}
                    useData={target.useData}
                    multiYearData={multiYearData}
                    branding={branding}
                  />
                </div>
              ))}
            {facilitiesExcluded?.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                Please note that the following facilities are excluded from the report:{' '}
                {facilitiesExcluded?.map((facility) => facility?.name)?.join(', ')}
              </div>
            )}
            {showFab && (
              <div className='flex justify-center fixed bottom-4 right-1/2 translate-x-1/2 z-50'>
                <Button
                  variant='secondary'
                  size='icon'
                  className='rounded-full shadow-lg'
                  title='Scroll to top'
                  onClick={() =>
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    })
                  }>
                  <ArrowUp size={20} strokeWidth={1.5} />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { entities: consumptionEntities, subentities: consumptionSubentities, years, filter } = state.consumption || {};
  const usageTypeDetails = state?.admin?.adminPortal?.usageTypeDetails;
  const { subentities, entities, report } = state;
  const neededEntities =
    entities &&
    consumptionEntities &&
    _.orderBy(
      entities.filter(({ id }) => consumptionEntities.includes(id)),
      ['name'],
    );
  const neededSubentities =
    subentities &&
    consumptionSubentities &&
    _.orderBy(
      subentities.filter(({ id }) => consumptionSubentities.includes(id)),
      ['name'],
    );
  return {
    report: report?.details,
    fetchingConsumpData: state.showLoader || state.showLoader === undefined,
    consumptionFilter: filter || [],
    subentities: neededSubentities,
    entities: neededEntities,
    years: years || [],
    branding: state?.branding,
    showLoader: state?.showLoader,
    facilitiesExcluded: state?.facility?.list?.filter((facility) => facility.excludeFromReports),
    usageTypeDetails,
    totals: state?.consumption?.totalEmissionsByScope,
    orgTargets: state?.dashboard?.targets,
    multiYearData: state?.dashboard?.data?.multiYear,
    currentOrganisation: state?.currentOrganisation,
    orgDetails: state?.organisation?.details,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getReport: (id) => dispatch(REPORT_ACTIONS.getReport(id)),
  getFacilityMetrics: () => dispatch(FACILITY_ACTIONS.getFacilityMetrics()),
  getBenchmarkData: () => dispatch(CONSUMPTION_ACTIONS.getBenchmarkData()),
  toggleConsumptionFilter: (filter) => dispatch(CONSUMPTION_ACTIONS.toggleConsumptionFilter(filter)),
  getAllItemsFromDB: (table) => dispatch(ADMIN_ACTIONS.getAllItemsFromDB(table)),
  sumValues: (data, year, org, dataType) => dispatch(DASHBOARD_ACTIONS.sumValues(data, year, org, dataType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HTMLReport);
